// What the app infers from behaviour. Kept apart from what the user told us (Profile).
//
// Every inference carries the evidence behind it and a confidence, and the user can confirm or
// reject it (overrides). Rejected inferences have no effect. Turning learning off removes all
// effects. Nothing here is sent to a server.

import type { CookedEntry, Feedback, PersistedState } from '../state/schema';
import { mainProtein } from './ranking';
import { getRecipe } from './recipes';
import { RECIPE_META, CUISINE_LABELS, type Cuisine } from './recipeMeta';
import { displayName, type IngredientId } from './taxonomy';
import { plural } from './text';

export type InferenceKind = 'protein' | 'cuisine' | 'quick' | 'heat' | 'waste';

export interface Inference {
  /** Stable key, e.g. "protein:chicken", "cuisine:italian", "quick", "heat", "waste:spinach". */
  key: string;
  kind: InferenceKind;
  direction: 'more' | 'less';
  label: string;
  evidence: string;
  /** 0 to 1. Shown as a word, not a number. */
  confidence: number;
  status: 'inferred' | 'confirmed' | 'rejected';
}

export interface Learned {
  enabled: boolean;
  /** Per-recipe affinity from direct feedback, roughly -2..+2. */
  recipe: Map<string, number>;
  /** Fact-based recipe notes, used for explanations ("You rated it Make again"). */
  recipeFacts: Map<string, 'loved' | 'disliked' | 'cooked'>;
  inferences: Inference[];
  /** Effective weights after overrides, used by ranking. */
  effects: {
    protein: Map<string, number>;
    cuisine: Map<Cuisine, number>;
    preferQuick: number;
    avoidHeat: number;
    wasted: IngredientId[];
  };
}

type Inputs = Pick<PersistedState, 'cooked' | 'feedback' | 'inferenceOverrides'> & { learning: boolean; now?: Date };

const HALF_LIFE_DAYS = 60;
const MIN_EVENTS = 3;

function decay(at: string, now: Date): number {
  const days = Math.max(0, (now.getTime() - new Date(at).getTime()) / 86_400_000);
  return Math.pow(0.5, days / HALF_LIFE_DAYS);
}

interface Tally { sum: number; n: number; up: number; down: number }
const tally = (): Tally => ({ sum: 0, n: 0, up: 0, down: 0 });

export function confidenceWord(c: number): 'Strong' | 'Moderate' | 'Early' {
  return c >= 0.66 ? 'Strong' : c >= 0.4 ? 'Moderate' : 'Early';
}

export function learn({ cooked, feedback, inferenceOverrides, learning, now = new Date() }: Inputs): Learned {
  const recipe = new Map<string, number>();
  const recipeFacts = new Map<string, 'loved' | 'disliked' | 'cooked'>();
  const proteins = new Map<string, Tally>();
  const cuisines = new Map<Cuisine, Tally>();
  let quick = 0, quickN = 0, heat = 0, heatN = 0;
  const waste = new Map<IngredientId, number>();

  const bump = (recipeId: string, value: number, w: number) => {
    const r = getRecipe(recipeId);
    if (!r) return;
    recipe.set(recipeId, (recipe.get(recipeId) ?? 0) + value * w);
    const p = mainProtein(r);
    if (p) {
      const t = proteins.get(p) ?? tally();
      t.sum += value * w; t.n++; if (value > 0) t.up++; else if (value < 0) t.down++;
      proteins.set(p, t);
    }
    const c = RECIPE_META[recipeId]?.cuisine;
    if (c && c !== 'everyday') {
      const t = cuisines.get(c) ?? tally();
      t.sum += value * w; t.n++; if (value > 0) t.up++; else if (value < 0) t.down++;
      cuisines.set(c, t);
    }
  };

  for (const c of cooked as CookedEntry[]) {
    const w = decay(c.at, now);
    const value = c.rating === 'up' ? 1 : c.rating === 'down' ? -1.5 : 0.4;
    bump(c.recipeId, value, w);
    if (!recipeFacts.has(c.recipeId) || c.rating) recipeFacts.set(c.recipeId, c.rating === 'up' ? 'loved' : c.rating === 'down' ? 'disliked' : 'cooked');
    if (c.notes.includes('too_long') || c.notes.includes('too_much_work') || c.took === 'longer') { quick += w; quickN++; }
    if (c.notes.includes('too_spicy')) { heat += w; heatN++; }
  }
  for (const f of feedback as Feedback[]) {
    const w = decay(f.at, now);
    switch (f.type) {
      case 'not_today':
        bump(f.recipeId, f.reason === 'dont_like' ? -1 : -0.25, w);
        if (f.reason === 'too_long') { quick += w; quickN++; }
        if (f.reason === 'too_spicy') { heat += w; heatN++; }
        break;
      case 'skip': {
        bump(f.recipeId, -0.4, w);
        const r = getRecipe(f.recipeId);
        if (f.reason === 'no_time' && r && r.minutes > 25) { quick += 0.7 * w; quickN++; }
        break;
      }
      case 'swap': bump(f.from, -0.3, w); bump(f.to, 0.3, w); break;
      case 'chose': bump(f.recipeId, 0.5, w); break;
      case 'passed': for (const id of f.recipeIds) bump(id, -0.1, w); break;
      case 'wasted': waste.set(f.ingredientId, (waste.get(f.ingredientId) ?? 0) + 1); break;
      case 'used_up': break;
    }
  }

  const status = (key: string): Inference['status'] => inferenceOverrides[key] === 'confirmed' ? 'confirmed' : inferenceOverrides[key] === 'rejected' ? 'rejected' : 'inferred';
  const inferences: Inference[] = [];

  for (const [p, t] of proteins) {
    if (t.n < MIN_EVENTS) continue;
    const mean = t.sum / t.n;
    if (Math.abs(mean) < 0.3) continue;
    const name = displayName(p as IngredientId);
    inferences.push({
      key: `protein:${p}`, kind: 'protein', direction: mean > 0 ? 'more' : 'less',
      label: mean > 0 ? `You enjoy ${name} dishes` : `${capital(name)} dishes aren’t landing`,
      evidence: evidenceLine(t),
      confidence: conf(t.n, mean), status: status(`protein:${p}`),
    });
  }
  for (const [c, t] of cuisines) {
    if (t.n < MIN_EVENTS) continue;
    const mean = t.sum / t.n;
    if (Math.abs(mean) < 0.3) continue;
    inferences.push({
      key: `cuisine:${c}`, kind: 'cuisine', direction: mean > 0 ? 'more' : 'less',
      label: mean > 0 ? `You like ${CUISINE_LABELS[c]} food` : `Less ${CUISINE_LABELS[c]} food for now`,
      evidence: evidenceLine(t),
      confidence: conf(t.n, mean), status: status(`cuisine:${c}`),
    });
  }
  // Weighted totals (recent signals count more) set confidence; counts set the evidence text.
  if (quickN >= 2 && quick >= 1) {
    inferences.push({
      key: 'quick', kind: 'quick', direction: 'more', label: 'Quicker dinners suit you better',
      evidence: `${plural(quickN, 'time')} a meal took too long or was skipped for time`,
      confidence: Math.round(Math.min(0.9, quick / (quick + 2)) * 100) / 100, status: status('quick'),
    });
  }
  if (heatN >= 1 && heat >= 0.5) {
    inferences.push({
      key: 'heat', kind: 'heat', direction: 'less', label: 'Go easy on the spice',
      evidence: `${plural(heatN, 'meal')} felt too spicy`,
      confidence: Math.round(Math.min(0.9, heat / (heat + 1)) * 100) / 100, status: status('heat'),
    });
  }
  for (const [id, n] of waste) {
    if (n < 2) continue;
    inferences.push({
      key: `waste:${id}`, kind: 'waste', direction: 'less', label: `${capital(displayName(id))} often goes unused`,
      evidence: `Removed as unused ${plural(n, 'time')}`,
      confidence: Math.min(0.9, n / (n + 2)), status: status(`waste:${id}`),
    });
  }
  inferences.sort((a, b) => b.confidence - a.confidence);

  const effects: Learned['effects'] = { protein: new Map(), cuisine: new Map(), preferQuick: 0, avoidHeat: 0, wasted: [] };
  if (learning) {
    for (const i of inferences) {
      if (i.status === 'rejected') continue;
      const strength = (i.status === 'confirmed' ? 1 : i.confidence) * (i.direction === 'more' ? 1 : -1);
      const [, value] = i.key.split(':');
      if (i.kind === 'protein') effects.protein.set(value, strength);
      if (i.kind === 'cuisine') effects.cuisine.set(value as Cuisine, strength);
      if (i.kind === 'quick') effects.preferQuick = Math.abs(strength);
      if (i.kind === 'heat') effects.avoidHeat = Math.abs(strength);
      if (i.kind === 'waste') effects.wasted.push(value as IngredientId);
    }
  }

  return {
    enabled: learning,
    recipe: learning ? recipe : new Map(),
    recipeFacts,
    inferences,
    effects,
  };
}

function conf(n: number, mean: number): number {
  return Math.round(Math.min(0.95, (n / (n + 4)) * Math.min(1, Math.abs(mean) * 1.5)) * 100) / 100;
}

function evidenceLine(t: Tally): string {
  const parts = [`From ${plural(t.n, 'meal or choice', 'meals and choices')}`];
  if (t.up) parts.push(`${t.up} went well`);
  if (t.down) parts.push(`${t.down} didn’t`);
  return parts.join(', ');
}

function capital(s: string) {
  return s[0].toUpperCase() + s.slice(1);
}

export const EMPTY_LEARNED: Learned = {
  enabled: false, recipe: new Map(), recipeFacts: new Map(), inferences: [],
  effects: { protein: new Map(), cuisine: new Map(), preferQuick: 0, avoidHeat: 0, wasted: [] },
};
